import { FlexLayout } from '@deity/falcon-ui';
import {
  countMappedRequiredFields,
  countSchemaFields,
  mappingSummary
} from 'components/Footer/MappingSummaryFooter/helpers';
import type { FC } from 'react';
import { useMemo } from 'react';
import type { GetMappingQuery, GetModelQuery } from 'services/graphql/generated/operations';
import { MappingEdgeVisualizer } from './MappingEdgeVisualizer';
import { MappingStats } from './MappingStats';
import { ModelInfo } from './ModelInfo';

interface MappingSummaryProps {
  mappingData?: GetMappingQuery;
  sourceModelData?: GetModelQuery;
  targetModelData?: GetModelQuery;
}
export const MappingSummary: FC<MappingSummaryProps> = ({ mappingData, sourceModelData, targetModelData }) => {
  const sourceSchemaCount = useMemo(() => {
    if (sourceModelData?.getModel.schema) {
      return countSchemaFields(sourceModelData.getModel.schema).fields;
    }
  }, [sourceModelData?.getModel.schema]);
  const targetSchemaCount = useMemo(() => {
    if (targetModelData?.getModel.schema) {
      return countSchemaFields(targetModelData.getModel.schema).fields;
    }
  }, [targetModelData?.getModel.schema]);

  const mappingCount = useMemo(() => {
    if (mappingData?.getMapping.schema) {
      return countSchemaFields(mappingData?.getMapping.schema).fields;
    }
  }, [mappingData?.getMapping.schema]);

  const processorCount = useMemo(() => {
    if (mappingData?.getMapping.schema) {
      return countSchemaFields(mappingData.getMapping.schema).processors;
    }
  }, [mappingData?.getMapping.schema]);

  const requiredTargetFieldsCount = useMemo(() => {
    if (targetModelData?.getModel.schema) {
      return countSchemaFields(targetModelData.getModel.schema).requiredFields;
    }
  }, [targetModelData?.getModel.schema]);

  const requiredSourceFieldsCount = useMemo(() => {
    if (targetModelData?.getModel.schema) {
      return countSchemaFields(targetModelData.getModel.schema).requiredFields;
    }
  }, [targetModelData?.getModel.schema]);

  const mappedRequiredField = useMemo(() => {
    if (mappingData?.getMapping.schema && targetModelData?.getModel.schema) {
      return countMappedRequiredFields(mappingData.getMapping.schema, targetModelData?.getModel.schema);
    }
  }, [mappingData?.getMapping.schema, targetModelData?.getModel.schema]);

  const summary = useMemo(() => {
    if (mappingData?.getMapping.schema) {
      return mappingSummary(mappingData.getMapping.schema);
    }
  }, [mappingData?.getMapping.schema]);

  return (
    <FlexLayout justifyContent="space-between" alignItems="center" flexWrap="nowrap" css={{ position: 'relative' }}>
      <ModelInfo
        name={sourceModelData?.getModel.name || '-'}
        fieldCount={sourceSchemaCount || 0}
        requiredFields={requiredTargetFieldsCount || 0}
      />
      <MappingEdgeVisualizer mappingEdges={summary?.mappingEdges || []} />
      <ModelInfo
        name={targetModelData?.getModel.name || '-'}
        fieldCount={targetSchemaCount || 0}
        requiredFields={requiredSourceFieldsCount || 0}
      />
      <MappingStats
        mappingCount={mappingCount || 0}
        processorCount={processorCount || 0}
        fieldsWithProcessor={summary?.fieldsWithProcessor || 0}
        requiredTargetField={requiredTargetFieldsCount || 0}
        mappedRequiredField={mappedRequiredField || 0}
      />
    </FlexLayout>
  );
};
